/**
 * Lazy-chunk load failure detection (post-deploy version skew).
 *
 * Vite emits content-hashed chunks and the app lazy-loads plenty of them
 * (route splits, dialogs, the header badge). A deploy replaces those files.
 * Any tab that was already open when the deploy landed then requests a hash
 * that no longer exists on the CDN, gets a 404, and the dynamic import
 * rejects. Deploys are frequent here (direct-to-prod is the default), so the
 * exposure window recurs several times a day.
 *
 * Caught live 2026-08-02 (release-gate run 30748221525): page loaded 12:38,
 * deploy 23537529 went live 12:42:01, page then died on
 *   TypeError: Failed to fetch dynamically imported module:
 *   https://spotivibly.com/assets/UnseenReconciliationBadge-BDxktuyG.js
 * plus two 404s, caught by [ErrorBoundary].
 *
 * This module is the single source of truth for "is this that class of
 * error". Three call sites share it so they cannot drift apart, which
 * design-rules.md §12.6 requires of the Sentry / client_errors pair:
 *   - src/components/ErrorBoundary.tsx  -> offer a reload instead of a
 *     generic "something went wrong" screen
 *   - src/instrument.ts                 -> drop from Sentry (not an app bug)
 *   - src/lib/errorLogger.ts            -> drop from client_errors (ditto)
 */

/**
 * Lowercase substrings. A message matches if it CONTAINS any of them.
 *
 * The wording differs per engine for what is one underlying failure, so the
 * list is deliberately per-browser rather than a single clever regex:
 *
 *   Chrome / Edge   "Failed to fetch dynamically imported module: <url>"
 *   Firefox         "error loading dynamically imported module: <url>"
 *   Safari / WebKit "Importing a module script failed."
 *   Chrome (MIME)   "Failed to load module script: Expected a JavaScript
 *                    module script but the server responded with a MIME type
 *                    of text/html." — what you get when the SPA rewrite
 *                    answers a missing /assets/*.js with the index document.
 *   Safari (MIME)   "...is not a valid JavaScript MIME type."
 *   webpack         "ChunkLoadError" / "Loading chunk 12 failed" — not
 *                    emitted by Vite, kept because third-party bundles
 *                    embedded on the page do throw it and the recovery is
 *                    identical.
 *   Vite preload    "Unable to preload CSS for /assets/index-XYZ.css" — the
 *                    modulepreload helper hitting the same stale hash
 *                    (SPOTIVIBLY-WEB-17, 2026-05-19).
 *
 * DELIBERATELY ABSENT: a bare "failed to fetch" / "load failed". Those are
 * ordinary network blips (Supabase call in an effect, cellular dropout) and
 * a reload does not fix them. The pre-2026-08-02 ErrorBoundary matched bare
 * "Failed to fetch", so a Supabase outage hard-reloaded every open tab.
 */
export const CHUNK_LOAD_ERROR_PATTERNS: readonly string[] = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "module script failed",
  "failed to load module script",
  "is not a valid javascript",
  "loading chunk",
  "chunkloaderror",
  "unable to preload css",
];

/**
 * Substring test against an already-extracted message string. Used by the
 * two telemetry filters, which only ever hold a string.
 */
export function matchesChunkLoadPattern(message: string): boolean {
  const normalized = message.toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

/**
 * Test a thrown value. Checks `name` as well as `message` so webpack's
 * `ChunkLoadError` (whose message is sometimes just the chunk id) is caught
 * by its name alone.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  if (error instanceof Error) {
    return matchesChunkLoadPattern(`${error.name}: ${error.message}`);
  }

  if (typeof error === "string") {
    return matchesChunkLoadPattern(error);
  }

  return false;
}
